import { useMutation, useQuery } from '@tanstack/react-query';

import { ConventionAvailabilityResponse } from '../models/ConventionAvailabilityResponse';
import { DateTime } from 'luxon';
import { EanEligibilityOutResponse } from '../models/EanEligibilityOutResponse';
import { EnergySharingVolumeResponse } from '../models/EnergySharingVolumeResponse';
import { IndexAndVolumeResponse } from '../models/IndexAndVolumeResponse';
import { LastIndexAndVolumeResponse } from '../models/LastIndexVolumeResponse';
import { MeterRequest } from '../models/MeterRequest';
import axiosClient from '../axiosBearerClient';

export const useGetEanValidate = (ean?: string | null) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['ean-validate'],
    gcTime: 0,
    enabled: false,
    retry: false,
    queryFn: async () => {
      return await axiosClient.get<EanEligibilityOutResponse>(`/meters/${ean}/validate`);
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetConventionAvailability = (ean?: string | null) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['convention-availability'],
    gcTime: 0,
    enabled: false,
    retry: false,
    queryFn: async () => {
      return await axiosClient.get<ConventionAvailabilityResponse>(
        `/meters/conventions/${ean}/availability`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useUpdateConvention = () => {
  const { isPending, error, data, isError, mutateAsync } = useMutation({
    retry: false,
    mutationFn: async ({
      sdpId,
      hasReadingConsent,
    }: {
      sdpId: string;
      hasReadingConsent: boolean;
    }) => {
      return await axiosClient.put<any>(`/meters/convention/${sdpId}`, {
        consent: hasReadingConsent,
      });
    },
  });

  return {
    isPending,
    error,
    isError,
    data,
    mutateAsync,
  };
};

export const usePostValidateEan = () => {
  const { isPending, error, data, isError, mutateAsync } = useMutation({
    retry: false,
    mutationFn: async (request: MeterRequest) => {
      return await axiosClient.post<EanEligibilityOutResponse>(`/meters/validate`, request);
    },
  });

  return {
    isPending,
    error,
    isError,
    data,
    mutateAsync,
  };
};

export const useGetIndex = ({
  sdpId,
  startDate,
  endDate,
}: {
  sdpId?: string | null;
  startDate?: string;
  endDate: string;
}) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['meter-index', sdpId],
    enabled: !!sdpId && !!startDate && !!endDate,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      return await axiosClient.get<IndexAndVolumeResponse[]>(
        `/meters/${sdpId}/index-view?startDate=${startDate!}&endDate=${endDate}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetEnergySharingOffTakes = ({
  sdpId,
  startDate,
  endDate,
  period,
  enabled,
}: {
  sdpId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  period?: string | null;
  enabled?: boolean;
}) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['meter-energy-sharing-off-fake', sdpId, startDate, endDate, period, enabled],
    enabled: !!sdpId && !!startDate && !!endDate && !!period && enabled,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const newStartDate = startDate
        ? DateTime.fromISO(startDate!)
            .setZone('Europe/Brussels')
            .startOf('day')
            .toJSDate()
            .toISOString()
        : startDate;
      const newEndDate = endDate
        ? DateTime.fromISO(endDate!)
            .setZone('Europe/Brussels')
            .endOf('day')
            .plus({ minutes: 15 })
            .toJSDate()
            .toISOString()
        : endDate;
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', newStartDate!);
      }
      if (endDate) {
        params.append('endDate', newEndDate!);
      }
      if (period) {
        params.append('period', period);
      }
      return await axiosClient.get<EnergySharingVolumeResponse[]>(
        `/meters/${sdpId}/energy-sharing/off-takes?${params.toString()}
				`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetEnergySharingInjections = ({
  sdpId,
  startDate,
  endDate,
  period,
  enabled,
}: {
  sdpId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  period?: string | null;
  enabled?: boolean;
}) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['meter-energy-sharing-injections', sdpId, startDate, endDate, period, enabled],
    enabled: !!sdpId && !!startDate && !!endDate && !!period && enabled,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const newStartDate = startDate
        ? DateTime.fromISO(startDate!)
            .setZone('Europe/Brussels')
            .startOf('day')
            .toJSDate()
            .toISOString()
        : startDate;
      const newEndDate = endDate
        ? DateTime.fromISO(endDate!)
            .setZone('Europe/Brussels')
            .endOf('day')
            .plus({ minutes: 15 })
            .toJSDate()
            .toISOString()
        : endDate;
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', newStartDate!);
      }
      if (endDate) {
        params.append('endDate', newEndDate!);
      }
      if (period) {
        params.append('period', period);
      }
      return await axiosClient.get<EnergySharingVolumeResponse[]>(
        `/meters/${sdpId}/energy-sharing/injections?${params.toString()}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetIndexedVolumes = ({
  sdpId,
  startDate,
  endDate,
}: {
  sdpId?: string | null;
  startDate?: string | null;
  endDate?: string;
}) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['meter-index-volumes', sdpId],
    enabled: !!sdpId,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', startDate!);
      }
      if (endDate) {
        params.append('endDate', endDate!);
      }
      return await axiosClient.get<LastIndexAndVolumeResponse[]>(
        `/meters/${sdpId}/index-view/volumes?${params.toString()}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};
